<template>
  <div class="template-designer-container">
    <!-- 顶部操作栏 -->
    <div class="designer-header">
      <el-form :inline="true" class="template-form">
        <el-form-item label="模板名称">
          <el-input
            v-model="templateData.name"
            placeholder="请输入模板名称"
            style="width: 200px"
          ></el-input>
        </el-form-item>
        <el-form-item label="模板描述">
          <el-input
            v-model="templateData.description"
            placeholder="请输入模板描述"
            style="width: 300px"
          ></el-input>
        </el-form-item>
      </el-form>
      <div class="header-actions">
        <el-button @click="previewTemplate">预览</el-button>
        <el-button type="primary" @click="saveTemplate">保存模板</el-button>
        <el-button @click="goBack">返回列表</el-button>
      </div>
    </div>

    <div class="designer-content">
      <!-- 左侧设计区域 -->
      <div class="design-panel">
        <div
          ref="templatePreviewRef"
          class="template-preview"
          :style="templateStyles"
        >
          <!-- 表头区域 -->
          <div class="template-header" v-if="showSections.header">
            <div
              class="header-title"
              v-if="getVisibleHeaderFields().some((f) => f.key === 'title')"
            >
              <span>{{ getInfoPlaceholder("title") }}</span>
            </div>
            <div class="header-info">
              <div
                v-for="field in getVisibleHeaderFields().filter(
                  (f) => f.key !== 'title'
                )"
                :key="field.key"
                class="info-item"
              >
                {{ field.label }}: {{ info[field.key] }}
              </div>
            </div>
          </div>

          <!-- 表格区域 -->
          <div class="template-table" v-if="showSections.table">
            <table class="goods-table">
              <thead>
                <tr>
                  <th v-for="field in getVisibleTableFields()" :key="field.key">
                    {{ field.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="field in getVisibleTableFields()" :key="field.key">
                    {{ getItemPlaceholder(field.key) }}
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="showSections.tableFooter">
                <tr>
                  <td
                    :colspan="getVisibleTableFields().length - 1"
                    class="text-right"
                  >
                    合计:
                  </td>
                  <td class="total-amount">{{ info.totalAmount }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- 表尾区域 -->
          <div class="template-footer" v-if="showSections.footer">
            <div class="footer-info">
              <div
                v-for="field in getVisibleFooterFields()"
                :key="field.key"
                class="footer-item"
              >
                <span v-if="field.key === 'signature'">
                  {{ field.label }}: ________________
                </span>
                <span v-else> {{ field.label }}: {{ info[field.key] }} </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧配置面板 -->
      <div class="config-panel">
        <el-tabs v-model="activeConfigTab">
          <!-- 区域设置 -->
          <el-tab-pane label="区域设置" name="sections">
            <div class="config-section">
              <h4>显示区域</h4>
              <el-checkbox v-model="showSections.header">表头区域</el-checkbox>
              <el-checkbox v-model="showSections.table">表格区域</el-checkbox>
              <el-checkbox v-model="showSections.tableFooter"
                >表格合计</el-checkbox
              >
              <el-checkbox v-model="showSections.footer">表尾区域</el-checkbox>
            </div>
          </el-tab-pane>

          <!-- 表头字段 -->
          <el-tab-pane label="表头字段" name="header">
            <div class="config-section">
              <h4>表头字段设置</h4>
              <div class="field-config">
                <div
                  v-for="field in availableHeaderFields"
                  :key="field.key"
                  class="field-item"
                >
                  <el-checkbox v-model="field.show">{{
                    field.label
                  }}</el-checkbox>
                  <span class="field-placeholder">{{
                    getInfoPlaceholder(field.key)
                  }}</span>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- 表格字段 -->
          <el-tab-pane label="表格字段" name="table">
            <div class="config-section">
              <h4>表格字段设置</h4>
              <div class="field-config">
                <div
                  v-for="field in availableTableFields"
                  :key="field.key"
                  class="field-item"
                >
                  <el-checkbox v-model="field.show">{{
                    field.label
                  }}</el-checkbox>
                  <span class="field-placeholder">{{
                    getItemPlaceholder(field.key)
                  }}</span>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- 表尾字段 -->
          <el-tab-pane label="表尾字段" name="footer">
            <div class="config-section">
              <h4>表尾字段设置</h4>
              <div class="field-config">
                <div
                  v-for="field in availableFooterFields"
                  :key="field.key"
                  class="field-item"
                >
                  <el-checkbox v-model="field.show">{{
                    field.label
                  }}</el-checkbox>
                  <span class="field-placeholder">{{
                    getInfoPlaceholder(field.key)
                  }}</span>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- 样式设置 -->
          <el-tab-pane label="样式设置" name="style">
            <div class="config-section">
              <h4>页面设置</h4>
              <el-form label-width="80px" size="small">
                <el-form-item label="纸张大小">
                  <el-select v-model="styleConfig.paperSize">
                    <el-option label="A4" value="A4"></el-option>
                    <el-option label="A5" value="A5"></el-option>
                    <el-option label="Letter" value="Letter"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="页面方向">
                  <el-radio-group v-model="styleConfig.orientation">
                    <el-radio label="portrait">纵向</el-radio>
                    <el-radio label="landscape">横向</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item label="字体大小">
                  <el-input-number
                    v-model="styleConfig.fontSize"
                    :min="10"
                    :max="20"
                  ></el-input-number>
                </el-form-item>
                <el-form-item label="标题大小">
                  <el-input-number
                    v-model="styleConfig.titleSize"
                    :min="16"
                    :max="32"
                  ></el-input-number>
                </el-form-item>
              </el-form>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog
      title="模板预览"
      :visible.sync="previewVisible"
      width="80%"
      top="5vh"
    >
      <div class="preview-actions">
        <el-button type="primary" @click="exportTemplate">导出模板</el-button>
        <el-button type="success" @click="printTemplate"><i class="el-icon-printer"></i> 打印</el-button>
        <el-button @click="previewVisible = false">关闭</el-button>
      </div>
      <div class="preview-content">
        <pre>{{ renderedTemplate }}</pre>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "TemplateDesigner",
  data() {
    return {
      templateId: null,
      templateData: {
        name: "",
        description: "",
      },
      activeConfigTab: "sections",

      // 区域显示控制
      showSections: {
        header: true,
        table: true,
        tableFooter: true,
        footer: true,
      },

      // 可用的表头字段（从后端获取）
      availableHeaderFields: [
        { key: "title", label: "标题", show: true },
        { key: "companyName", label: "公司名称", show: true },
        { key: "customerName", label: "客户名称", show: true },
        { key: "deliveryDate", label: "配送日期", show: true },
        { key: "orderNo", label: "订单号", show: true },
        { key: "address", label: "配送地址", show: true },
        { key: "contact", label: "联系人", show: true },
        { key: "phone", label: "联系电话", show: true },
        { key: "totalAmount", label: "总金额", show: false },
      ],

      // 可用的表格字段（从后端获取）
      availableTableFields: [
        { key: "index", label: "序号", show: true },
        { key: "goodsName", label: "商品名称", show: true },
        { key: "specification", label: "规格", show: true },
        { key: "unit", label: "单位", show: true },
        { key: "quantity", label: "数量", show: true },
        { key: "price", label: "单价", show: true },
        { key: "amount", label: "金额", show: true },
        { key: "remark", label: "备注", show: true },
        { key: "brand", label: "品牌", show: false },
        { key: "category", label: "分类", show: false },
      ],

      // 可用的表尾字段（从后端获取）
      availableFooterFields: [
        { key: "deliveryCompany", label: "配送单位", show: true },
        { key: "deliveryPerson", label: "配送员", show: true },
        { key: "deliveryTime", label: "配送时间", show: true },
        { key: "signature", label: "客户签字", show: true },
        { key: "remark", label: "备注", show: true },
        { key: "totalWeight", label: "总重量", show: false },
      ],

      // 样式配置
      styleConfig: {
        paperSize: "A4",
        orientation: "portrait",
        fontSize: 12,
        titleSize: 18,
      },

      previewVisible: false,
      renderedTemplate: "",
    };
  },

  computed: {
    // 模板样式
    templateStyles() {
      return {
        fontSize: this.styleConfig.fontSize + "px",
        width: this.styleConfig.paperSize === "A4" ? "210mm" : "148mm",
        minHeight: this.styleConfig.paperSize === "A4" ? "297mm" : "210mm",
      };
    },

    // 占位符数据 - 用于预览显示
    info() {
      const placeholders = {};
      this.availableHeaderFields.forEach((field) => {
        placeholders[field.key] = `{{info.${field.key}}}`;
      });
      this.availableFooterFields.forEach((field) => {
        if (!placeholders[field.key]) {
          placeholders[field.key] = `{{info.${field.key}}}`;
        }
      });
      return placeholders;
    },
  },

  created() {
    // 检查是否是编辑模式
    if (this.$route.query.id) {
      this.templateId = this.$route.query.id;
      this.loadTemplate();
    } else {
      // 新建模式，设置默认值
      this.templateData.name = "新建发货单模板";
      this.templateData.description = "请输入模板描述";
    }

    // 模拟从后端获取字段配置
    this.loadFieldsFromBackend();
  },

  methods: {
    // 生成info占位符
    getInfoPlaceholder(fieldKey) {
      return `{{info.${fieldKey}}}`;
    },

    // 生成item占位符
    getItemPlaceholder(fieldKey) {
      return `{{item.${fieldKey}}}`;
    },

    // 模拟从后端获取字段配置
    async loadFieldsFromBackend() {
      // 这里应该调用后端API获取字段配置
      // const response = await this.$api.getTemplateFields()
      // this.availableHeaderFields = response.headerFields
      // this.availableTableFields = response.tableFields
      // this.availableFooterFields = response.footerFields

      console.log("从后端加载字段配置...");
    },

    // 获取可见的表头字段
    getVisibleHeaderFields() {
      return this.availableHeaderFields.filter((field) => field.show);
    },

    // 获取可见的表格字段
    getVisibleTableFields() {
      return this.availableTableFields.filter((field) => field.show);
    },

    // 获取可见的表尾字段
    getVisibleFooterFields() {
      return this.availableFooterFields.filter((field) => field.show);
    },

    // 加载模板
    loadTemplate() {
      const templates = JSON.parse(
        localStorage.getItem("deliveryTemplates") || "[]"
      );
      const template = templates.find((t) => t.id === this.templateId);

      if (template) {
        this.templateData = {
          name: template.name,
          description: template.description,
        };

        // 恢复配置
        if (template.config) {
          this.showSections = {
            ...this.showSections,
            ...template.config.showSections,
          };
          this.styleConfig = {
            ...this.styleConfig,
            ...template.config.styleConfig,
          };

          // 恢复字段显示状态
          if (template.config.headerFields) {
            template.config.headerFields.forEach((savedField) => {
              const field = this.availableHeaderFields.find(
                (f) => f.key === savedField.key
              );
              if (field) {
                field.show = savedField.show;
              }
            });
          }

          if (template.config.tableFields) {
            template.config.tableFields.forEach((savedField) => {
              const field = this.availableTableFields.find(
                (f) => f.key === savedField.key
              );
              if (field) {
                field.show = savedField.show;
              }
            });
          }

          if (template.config.footerFields) {
            template.config.footerFields.forEach((savedField) => {
              const field = this.availableFooterFields.find(
                (f) => f.key === savedField.key
              );
              if (field) {
                field.show = savedField.show;
              }
            });
          }
        }
      } else {
        this.$message.error("模板不存在");
      }
    },

    // 生成模板HTML
    generateTemplateHtml() {
      let html = '<div class="delivery-template">';

      // 表头
      if (this.showSections.header) {
        html += '<div class="template-header">';

        const titleField = this.availableHeaderFields.find(
          (f) => f.key === "title" && f.show
        );
        if (titleField) {
          html += '<div class="header-title">{{info.title}}</div>';
        }

        html += '<div class="header-info">';
        this.getVisibleHeaderFields()
          .filter((f) => f.key !== "title")
          .forEach((field) => {
            html += `<div class="info-item">${field.label}: {{info.${field.key}}}</div>`;
          });
        html += "</div></div>";
      }

      // 表格
      if (this.showSections.table) {
        html += '<table class="goods-table"><thead><tr>';

        // 表头
        this.getVisibleTableFields().forEach((field) => {
          html += `<th>${field.label}</th>`;
        });

        html += "</tr></thead><tbody>{{#each list}}<tr>";

        // 数据行
        this.getVisibleTableFields().forEach((field) => {
          html += `<td>{{item.${field.key}}}</td>`;
        });

        html += "</tr>{{/each}}</tbody>";

        // 表格合计
        if (this.showSections.tableFooter) {
          const visibleColumns = this.getVisibleTableFields().length;
          html += "<tfoot><tr>";
          html += `<td colspan="${
            visibleColumns - 1
          }" class="text-right">合计:</td>`;
          html += '<td class="total-amount">{{info.totalAmount}}</td>';
          html += "</tr></tfoot>";
        }

        html += "</table>";
      }

      // 表尾
      if (this.showSections.footer) {
        html += '<div class="template-footer"><div class="footer-info">';
        this.getVisibleFooterFields().forEach((field) => {
          if (field.key === "signature") {
            html += `<div class="footer-item">${field.label}: ________________</div>`;
          } else {
            html += `<div class="footer-item">${field.label}: {{info.${field.key}}}</div>`;
          }
        });
        html += "</div></div>";
      }

      html += "</div>";
      return html;
    },

    // 预览模板
    previewTemplate() {
      // 使用完整HTML（包含CSS）进行预览
      const fullHtml = this.generateFullHtml();
      this.renderedTemplate = fullHtml;
      this.previewVisible = true;
    },

    // 导出模板
    exportTemplate() {
      // 使用完整HTML（包含CSS）
      const fullHtml = this.generateFullHtml();

      // 创建下载链接
      const blob = new Blob([fullHtml], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${this.templateData.name || "模板"}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      this.$message.success("模板导出成功");
    },
    
    // 打印模板
    printTemplate() {
      // 使用完整HTML（包含CSS）
      const fullHtml = this.generateFullHtml();
      
      // 直接使用window.open方法打开新窗口进行打印
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        this.$message.error('打印窗口被浏览器阻止，请允许弹出窗口后重试');
        return;
      }
      
      // 写入HTML内容
      printWindow.document.open();
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      
      // 添加打印样式
      const style = printWindow.document.createElement('style');
      style.textContent = `
        @media print {
          body { margin: 0; padding: 0; }
          .delivery-template { padding: 0; }
          .goods-table { page-break-inside: avoid; }
        }
      `;
      printWindow.document.head.appendChild(style);
      
      // 等待内容加载完成后打印
      printWindow.onload = function() {
        try {
          // 自动调用打印
          setTimeout(() => {
            printWindow.print();
            // 打印完成后关闭窗口
            setTimeout(() => {
              printWindow.close();
            }, 500);
          }, 500);
        } catch (e) {
          console.error('打印失败:', e);
          // 如果打印失败，保持窗口打开，让用户手动打印
        }
      };
    },

    // 生成完整HTML（包含CSS）
    generateFullHtml() {
      // 提取当前应用的样式
      const styles = `
<style>
.delivery-template {
  font-family: Arial, sans-serif;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
  font-size: ${this.styleConfig.fontSize}px;
}

.template-header {
  text-align: center;
  margin-bottom: 30px;
}

.header-title {
  font-size: ${this.styleConfig.titleSize}px;
  font-weight: bold;
  margin-bottom: 20px;
}

.header-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  text-align: left;
}

.info-item {
  padding: 5px 0;
}

.template-table {
  margin: 20px 0;
  width: 100%;
}

.goods-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.goods-table th,
.goods-table td {
  border: 1px solid #333;
  padding: 4px 6px;
  text-align: center;
  word-break: break-word;
  min-width: 60px;
}

.goods-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.text-right {
  text-align: right;
}

.total-amount {
  font-weight: bold;
}

.template-footer {
  margin-top: 40px;
}

.footer-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.footer-item {
  padding: 5px 0;
}

@media print {
  .delivery-template {
    padding: 0;
  }
  
  .goods-table {
    page-break-inside: avoid;
  }
}
</style>`;

      // 组合完整的HTML文档
      const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.templateData.name}</title>
  ${styles}
</head>
<body>
  <div class="delivery-template">
    ${this.generateTemplateHtml()}
  </div>
</body>
</html>`;

      return fullHtml;
    },

    // 保存模板
    saveTemplate() {
      if (!this.templateData.name.trim()) {
        this.$message.error("请输入模板名称");
        return;
      }

      const templateHtml = this.generateTemplateHtml();
      const fullHtml = this.generateFullHtml();

      const saveData = {
        id: this.templateId || Date.now().toString(),
        name: this.templateData.name,
        description: this.templateData.description,
        content: templateHtml,
        fullHtml: fullHtml, // 保存完整HTML（包含CSS）
        config: {
          showSections: { ...this.showSections },
          headerFields: this.availableHeaderFields.map((f) => ({
            key: f.key,
            show: f.show,
            label:f.label,
          })),
          tableFields: this.availableTableFields.map((f) => ({
            key: f.key,
            show: f.show,
            label:f.label,
          })),
          footerFields: this.availableFooterFields.map((f) => ({
            key: f.key,
            show: f.show,
            label:f.label,
          })),
          styleConfig: { ...this.styleConfig },
        },
        createdAt: this.templateId ? undefined : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      let templates = JSON.parse(
        localStorage.getItem("deliveryTemplates") || "[]"
      );

      if (this.templateId) {
        // 更新现有模板
        const index = templates.findIndex((t) => t.id === this.templateId);
        if (index !== -1) {
          templates[index] = { ...templates[index], ...saveData };
        } else {
          templates.push(saveData);
        }
      } else {
        // 添加新模板
        templates.push(saveData);
      }

      localStorage.setItem("deliveryTemplates", JSON.stringify(templates));

      this.$message.success("模板保存成功");

      // 如果是新建模板，更新ID
      if (!this.templateId) {
        this.templateId = saveData.id;
        this.$router.replace({
          path: this.$route.path,
          query: { ...this.$route.query, id: this.templateId },
        });
      }
    },

    // 返回列表
    goBack() {
      this.$router.push("/delivery/template-list");
    },
  },
};
</script>

<style scoped>
.template-designer-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.designer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}

.template-form {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.designer-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.design-panel {
  flex: 1;
  padding: 20px;
  background: #f5f5f5;
  overflow: auto;
}

.template-preview {
  background: white;
  margin: 0 auto;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  min-height: 600px;
  max-width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
}

.template-header {
  text-align: center;
  margin-bottom: 30px;
}

.header-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

.header-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  text-align: left;
}

.info-item {
  padding: 5px 0;
}

.template-table {
  margin: 20px 0;
  overflow-x: auto;
  width: 100%;
}

.goods-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  font-size: 12px;
}

.goods-table th,
.goods-table td {
  border: 1px solid #333;
  padding: 4px 6px;
  text-align: center;
  word-break: break-word;
  min-width: 60px;
}

.goods-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  font-size: 11px;
}

.text-right {
  text-align: right;
}

.total-amount {
  font-weight: bold;
}

.template-footer {
  margin-top: 40px;
}

.footer-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.footer-item {
  padding: 5px 0;
}

.config-panel {
  width: 350px;
  background: white;
  border-left: 1px solid #e8e8e8;
  overflow: auto;
}

.config-section {
  padding: 20px;
}

.config-section h4 {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #333;
}

.field-config {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.field-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.field-placeholder {
  font-size: 12px;
  color: #999;
  font-family: monospace;
}

.preview-actions {
  margin-bottom: 20px;
  text-align: right;
}

.preview-content {
  border: 1px solid #ddd;
  padding: 20px;
  background: white;
  max-height: 500px;
  overflow: auto;
}

.preview-content pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .config-panel {
    width: 300px;
  }
}

@media (max-width: 768px) {
  .designer-content {
    flex-direction: column;
  }

  .config-panel {
    width: 100%;
    height: 300px;
  }

  .template-preview {
    padding: 20px;
  }

  .header-info,
  .footer-info {
    grid-template-columns: 1fr;
  }
}
</style>